
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { PrayerRequestListItem } from "./PrayerRequestListItem";
import { EmptyPrayerRequests } from "./EmptyPrayerRequests";
import { usePrayerLikes } from "./hooks/usePrayerLikes";
import { ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@supabase/auth-helpers-react";

interface PrayerRequest {
  id: number;
  title: string;
  content: string;
  created_at: string;
  user_id: string;
  is_anonymous?: boolean;
}

interface Props {
  prayerRequests: PrayerRequest[];
  comments: Record<number, any[]>;
  isAdmin: boolean;
  onDeleteRequest: (id: number) => void;
  onCommentAdded: () => void;
  isSignedIn?: boolean;
  onSignInPrompt?: () => void;
}

export function PrayerRequestList({ 
  prayerRequests, 
  comments, 
  isAdmin, 
  onDeleteRequest,
  onCommentAdded,
  isSignedIn = true,
  onSignInPrompt
}: Props) {
  const { toast } = useToast();
  const session = useSession();
  const [reportedRequests, setReportedRequests] = useState<Record<number, boolean>>({});
  const [displayCount, setDisplayCount] = useState(5);
  const { likes, userLikes, handleLike } = usePrayerLikes(prayerRequests);

  const handleReport = async (type: 'prayer' | 'comment', id: number) => {
    setReportedRequests(prev => ({
      ...prev,
      [id]: true
    }));

    toast({
      title: "Report Submitted",
      description: "Thank you for helping keep our community safe.",
    });
  };

  const handleAmen = async (requestId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!session?.user) {
      toast({
        title: "Prayer Recorded 🙏",
        description: "Thank you for your heart to pray! Your intentions are valued.",
      });
      return;
    }

    try {
      // Add a comment indicating the user prayed for this request
      const { error } = await supabase
        .from('prayer_comments')
        .insert([
          {
            content: "🙏 I saw this request and prayed for you. God bless!",
            prayer_request_id: requestId,
            user_id: session.user.id,
          }
        ]);

      if (error) {
        console.error('Error adding prayer comment:', error);
        toast({
          title: "Error",
          description: "Failed to record your prayer. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Prayer Recorded 🙏",
        description: "Thank you for praying! Your support has been added to the request.",
      });
      
      // Refresh the comments
      onCommentAdded();
    } catch (error) {
      console.error('Error recording prayer:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  const filteredRequests = prayerRequests.filter(request => !reportedRequests[request.id]);
  const displayedRequests = filteredRequests.slice(0, displayCount);
  const hasMore = filteredRequests.length > displayCount;

  const loadMore = () => {
    setDisplayCount(prev => prev + 10);
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-block relative">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-indigo-500/30 to-blue-500/20 blur-xl rounded-full"></div>
          <h2 className="relative text-4xl font-bold bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent mb-3 px-8 py-4">
            Community Prayer Requests
          </h2>
        </div>
        <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
          Share your prayer requests and join our community in lifting up these prayers together. Every prayer matters and makes a difference.
        </p>
      </div>
      
      {filteredRequests.length > 0 ? (
        <div className="space-y-6">
          {displayedRequests.map((request) => (
            <PrayerRequestListItem
              key={request.id}
              request={request}
              likes={likes[request.id] || 0}
              comments={comments[request.id] || []}
              isAdmin={isAdmin}
              userHasLiked={userLikes[request.id]}
              onLike={handleLike}
              onDelete={onDeleteRequest}
              onReport={handleReport}
              onCommentAdded={onCommentAdded}
              onAmen={handleAmen}
            />
          ))}
          
          {hasMore && (
            <div className="pt-8 text-center">
              <Button 
                onClick={loadMore}
                variant="outline"
                className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 hover:from-purple-500/20 hover:to-blue-500/20 border-purple-500/30 text-purple-700 hover:text-purple-700 font-semibold px-8 py-3 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 hover:scale-105"
              >
                <span>Show More Prayers</span>
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      ) : (
        <EmptyPrayerRequests />
      )}
    </div>
  );
}
